import { normalizeProductPageUrl } from '@/features/pdp/utils/pdpUrl';
import videoCallIcon from '@/shared/icons/video.svg';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useState } from 'react';
import viewSimilarIcon from '@/shared/icons/view-similar.svg';
import viewDetailsIcon from '@/shared/icons/view-details.svg';
import { Loader2, Play } from 'lucide-react';
import discountIcon from '@/shared/icons/discount.svg';
import { cn } from '@/shared/utils/cn';
import { WishlistIcon } from '@/shared/icons/icons';
import type { ListingDesignItem } from '../../domain/models/ProductListing';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import ProductVideoModal from './ProductVideoModal_v2';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { ProductCardMediaCarousel } from './ProductCardMediaCarousel';
import { RotatingProductDescription } from './RotatingProductDescription';

export interface ProductCardProps {
  /** Product/design item – use ListingDesignItem from any source (browse, search, recently-viewed) */
  product: ListingDesignItem;
  onViewSimilar?: (product: ListingDesignItem) => void;
  similarExpanded?: boolean;
  /** Called before navigating to PDP; pass designId for PLP back-restore anchor (mobile). */
  onBeforeNavigate?: (designId: number) => void;
  /** When true, open PDP in a new tab instead of the same window. */
  openInNewTab?: boolean;
  /** Analytics: called when user clicks the product card or View Details button */
  onProductClick?: (product: ListingDesignItem) => void;
  /** Analytics: eventLogger on wishlist icon tap (before API), browse PLP only in old-react */
  onWishlistClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user toggles the wishlist icon (after successful toggle) */
  onWishlistToggle?: (product: ListingDesignItem, action: 'add' | 'remove') => void;
  /** Analytics: called when user clicks the video play button */
  onVideoClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user clicks the View Details button specifically */
  onViewDetailsClick?: (product: ListingDesignItem) => void;
  onVideoCallCartAdd?: (product: ListingDesignItem, errorMsg?: string) => void;
  /** When true, hides the Book Video Call button (e.g. on goldcoins PLP). */
  hideVideoCallButton?: boolean;
  /** When true, hides the View Details button completely (for mobile & desktop). */
  hideViewDetailsButton?: boolean;
  /** Automatically advance this card's ordered carousel media while it is visible. */
  autoSlideMedia?: boolean;
  /** 1-based product position in the listing grid (eventLogger). */
  productPosition?: number;
  /** Optional custom badge rendered in the relative image container */
  badge?: React.ReactNode;
  /** Optional custom footer element (e.g. Add to Cart button) */
  footerCta?: React.ReactNode;
  /** Optional extra wrapper class name */
  className?: string;
}

export function ProductCard({
  product,
  onViewSimilar,
  similarExpanded = false,
  onBeforeNavigate,
  openInNewTab,
  onProductClick,
  onWishlistClick,
  onWishlistToggle,
  onVideoClick,
  onVideoCallCartAdd,
  onViewDetailsClick,
  autoSlideMedia = false,
  productPosition,
  hideVideoCallButton = false,
  hideViewDetailsButton = false,
  badge,
  footerCta,
  className,
}: ProductCardProps) {
  const [videoOpen, setVideoOpen] = useState(false);
  const { analytics } = useAnalytics();
  const trackViewSimilar = () => analytics?.trackEvent('View Similar CTA Clicked', {
    'Product Id': String(product.designId),
    'Product Name': product.designName,
    'Category Name': product.categoryType ?? '',
  });
  const [heartAnimating, setHeartAnimating] = useState(false);
  const { isWishlisted, toggleWishlist } = useWishlistToggle();
  const { addToVideoCallCart, isLoading: isVideoCallLoading } = useVideoCallCart({ showToast: false });
  const { ref: visibilityRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.5,
    enabled: true,
  });

  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartAnimating(true);
    if (productPosition != null) {
      onWishlistClick?.(product, productPosition);
    }
    const action = isWishlisted(product.designId) ? 'remove' : 'add';
    const success = await toggleWishlist(product.designId);
    if (success) {
      onWishlistToggle?.(product, action);
    }
  };

  const pdpUrl = normalizeProductPageUrl(product.productPageUrl) ?? undefined;

  const navigateToPdp = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!pdpUrl) return;
    onProductClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) {
      window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = pdpUrl;
    }
  };

  const hasSimilarDesigns = product.hasSimilarDesigns === true;;
  const cardActionLabel = hasSimilarDesigns ? 'View Similar' : 'View Details';
  const handleCardAction = () => {
    if (hasSimilarDesigns) {
      if (!similarExpanded) trackViewSimilar();
      onViewSimilar?.(product);
      return;
    }
    if (!pdpUrl) return;
    onViewDetailsClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    else window.location.href = pdpUrl;
  };

  const wishlisted = isWishlisted(product.designId);
  const discountText = getDiscountText(product);

  return (
    <article
      ref={visibilityRef}
      className={cn(
        "group flex h-full select-none flex-col overflow-hidden rounded-md border border-gray-200 bg-white",
        className
      )}
    >
      <div className="relative aspect-square bg-[#F9F9FB]">
        {badge}
        {/* Wishlist button — top-left */}
        <button
          type="button"
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm" 
          onClick={handleWishlistClick}
        >
          <WishlistIcon
            filled={wishlisted}
            className={cn(
              'h-4 w-4 transition-colors',
              wishlisted ? 'text-[#B98599]' : 'text-gray-800',
              heartAnimating && 'animate-heart-pop',
            )}
            aria-hidden
            onAnimationEnd={() => setHeartAnimating(false)}
          />
        </button>

        {product.videoUrl && (
          <button
            type="button"
            aria-label="Play product video"
            className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              if (productPosition != null) {
                onVideoClick?.(product, productPosition);
              }
              setVideoOpen(true);
            }}
          >
            <Play className="h-3.5 w-3.5 text-gray-600" aria-hidden />
          </button>
        )}

        {product.carouselMedia?.length ? (
          <ProductCardMediaCarousel
            designId={product.designId}
            designName={product.designName}
            media={product.carouselMedia}
            isActive={isIntersecting}
            autoSlide={autoSlideMedia}
            showProgress
            onProductClick={navigateToPdp}
          />
        ) : product.imageUrl ? (
          <button
            type="button"
            onClick={navigateToPdp}
            className="relative block h-full w-full"
            aria-label={product.designName}
          >
            <img
              src={product.imageUrl}
              alt={product.designName}
              className="aspect-square h-full w-full object-cover"
              loading="lazy"
            />
          </button>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
            No image
          </div>
        )}

      </div>

      <div className="flex min-w-0 flex-1 flex-col px-3 pb-3 pt-2">
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <h2 className="mb-1 min-w-0 truncate text-xs font-medium text-[#AAA]">
            <a
              href={pdpUrl}
              title={product.designName}
              className="block truncate"
              onClick={(e) => { e.preventDefault(); navigateToPdp(); }}
            >
              {product.designName}
            </a>
          </h2>

          <p className="mb-1 text-base font-semibold leading-5 text-[#0D2456]">
            {product.discountedPrice || product.price}
          </p>

          <RotatingProductDescription product={product} className="mb-1 flex h-6 min-w-0 items-center overflow-hidden text-[12px] leading-4 text-[#595959]" />

          {discountText && (
            <p className="mb-2 flex min-w-0 items-center gap-1 text-[12px] leading-4 text-[#00852C]">
              <img src={discountIcon} alt="" aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span className="truncate" title={discountText}>{discountText}</span>
            </p>
          )}
        </div>

        {footerCta ? (
          <div className="shrink-0 pt-1">{footerCta}</div>
        ) : (
          (!hideViewDetailsButton || (product.customizationId != null && !hideVideoCallButton)) && (
            <div className="flex shrink-0 items-stretch gap-1.5 pt-1">
              {!hideViewDetailsButton && (
                <button
                  type="button"
                  aria-label={cardActionLabel}
                  aria-expanded={hasSimilarDesigns ? similarExpanded : undefined}
                  aria-controls={hasSimilarDesigns && similarExpanded ? `similar-designs-${product.designId}` : undefined}
                  className={cn("flex h-9 min-w-0 flex-1 items-center justify-center gap-1 overflow-hidden rounded-md border border-[#CBD6E8] px-1 text-[11px] font-medium text-[#0D2456] min-[400px]:text-[12px]", similarExpanded && "border-[#103064] bg-[#103064] text-white")}
                  onClick={handleCardAction}
                >
                  <img src={hasSimilarDesigns ? viewSimilarIcon : viewDetailsIcon} alt="" aria-hidden="true" className={cn("h-5 w-5 shrink-0", similarExpanded && "brightness-0 invert")} />
                  <span className="min-w-0 truncate">{cardActionLabel}</span>
                </button>
              )}
              {product.customizationId != null && !hideVideoCallButton && (
                <button
                  type="button"
                  aria-label="Add to video call cart"
                  className="flex h-9 w-10 shrink-0 items-center justify-center rounded-md border border-[#CBD6E8] text-[#16823D] disabled:opacity-50"
                  disabled={isVideoCallLoading}
                  onClick={async () => {
                    const res = await addToVideoCallCart(product.customizationId!);
                    onVideoCallCartAdd?.(product, res.success ? undefined : res.message);
                  }}
                >
                  {isVideoCallLoading
                    ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                    : <img src={videoCallIcon} alt="" aria-hidden="true" className="h-5 w-5" />}
                </button>
              )}
            </div>
          )
        )}
      </div>


      {product.videoUrl && (
        <ProductVideoModal
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          videoUrl={product.videoUrl}
          videoPosterUrl={product.videoPosterUrl}
          productName={product.designName}
          productPageUrl={product.productPageUrl}
        />
      )}
    </article>
  );
}

function getDiscountText(
  product: Pick<
    ListingDesignItem,
    'discountName' | 'discountValue' | 'discountPercent'
  >
): string {
  // If API provides a custom offer label (e.g. "70% off on Making Charges"), use it directly
  if (product.discountName) {
    return product.discountName;
  }

  // Fallback: "Save ₹X" style text when there is a numeric discount but no named offer
  if (product.discountPercent && product.discountPercent > 0 && product.discountValue) {
    return `Save ${product.discountValue}`;
  }

  return '';
}
